import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { ArrowLeft, ImagePlus } from 'lucide-react'
import { createService, getService, updateService } from '../api/services'
import { uploadImage } from '../api/upload'
import { useAuth } from '../context/AuthContext'
import styles from './ServiceForm.module.css'

export default function ServiceForm() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const { token, user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const fileRef = useRef(null)

  const [fetching, setFetching] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [thumbnail, setThumbnail] = useState('')
  const [error, setError] = useState(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: { cost: '' } })

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/login', { replace: true })
    }
  }, [user, authLoading, navigate])

  useEffect(() => {
    if (!isEdit || !token) return
    getService(token, id)
      .then((s) => {
        reset({ name: s.name, description: s.description, cost: s.cost })
        setThumbnail(s.thumbnail || '')
      })
      .catch(() => setError('Service not found.'))
      .finally(() => setFetching(false))
  }, [id, token, isEdit, reset])

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setError(null)
    try {
      const url = await uploadImage(file, token)
      setThumbnail(url)
    } catch {
      setError('Image upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const onSubmit = async (data) => {
    setSaving(true)
    setError(null)
    try {
      const payload = {
        name: data.name,
        description: data.description,
        cost: data.cost !== '' ? Number(data.cost) : 0,
        thumbnail,
      }
      if (isEdit) {
        await updateService(token, id, payload)
      } else {
        await createService(token, payload)
      }
      navigate('/services-manage')
    } catch (err) {
      const msgs = err.response?.data?.errors
      setError(msgs ? msgs.join(', ') : `Failed to ${isEdit ? 'save' : 'create'} service.`)
    } finally {
      setSaving(false)
    }
  }

  if (authLoading || !user) return null
  if (fetching) return <div style={{ padding: '48px 24px', color: 'var(--text-muted)' }}>Loading…</div>

  return (
    <main className={styles.page}>
      <Link to="/services-manage" className={styles.back}>
        <ArrowLeft size={16} />
        Back to Services
      </Link>
      <div className={styles.card}>
        <h1 className={styles.title}>{isEdit ? 'Edit Service' : 'New Service'}</h1>
        <p className={styles.subtitle}>
          {isEdit ? 'Update service details.' : 'Add a new service to your offerings.'}
        </p>

        {error && <div className={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className={styles.field}>
            <label className={styles.label}>Thumbnail</label>
            {thumbnail && (
              <img src={thumbnail} alt="Preview" className={styles.thumbnailPreview} />
            )}
            <button
              type="button"
              className={styles.uploadBtn}
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              <ImagePlus size={15} />
              {uploading ? 'Uploading…' : thumbnail ? 'Change Image' : 'Upload Image'}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className={styles.fileInput}
              onChange={handleFileChange}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Service Name *</label>
            <input
              className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
              placeholder="e.g. Web Design"
              {...register('name', { required: 'Name is required' })}
            />
            {errors.name && <p className={styles.fieldError}>{errors.name.message}</p>}
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Description</label>
            <textarea
              className={styles.textarea}
              rows={4}
              placeholder="Describe what this service includes…"
              {...register('description')}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Cost ($)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className={`${styles.input} ${errors.cost ? styles.inputError : ''}`}
              placeholder="e.g. 1500.00"
              {...register('cost', {
                min: { value: 0, message: 'Cost cannot be negative' },
              })}
            />
            {errors.cost && <p className={styles.fieldError}>{errors.cost.message}</p>}
          </div>

          <div className={styles.actions}>
            <button type="submit" className={styles.submitBtn} disabled={saving || uploading}>
              {saving
                ? isEdit ? 'Saving…' : 'Creating…'
                : isEdit ? 'Save Changes' : 'Create Service'}
            </button>
          </div>
        </form>
      </div>
    </main>
  )
}
